import { useEffect, Component, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Breadcrumbs from './components/Breadcrumbs';
import Home from './pages/Home';
import useAnalytics from './hooks/useAnalytics';
import './styles/global.css';

// Home, the navbar and the footer ship in the main bundle. Every other page loads on demand,
// and the admin panel (with its Excel and chart libraries) never loads for public visitors.
const About = lazy(() => import('./pages/About'));
const Program = lazy(() => import('./pages/Program'));
const Services = lazy(() => import('./pages/Services'));
const IdeaToImpact = lazy(() => import('./pages/IdeaToImpact'));
const IdeaToImpactApply = lazy(() => import('./pages/IdeaToImpactApply'));
const ProjectAssistance = lazy(() => import('./pages/ProjectAssistance'));
const Careers = lazy(() => import('./pages/Careers'));
const CareerDetail = lazy(() => import('./pages/CareerDetail'));
const Register = lazy(() => import('./pages/Register'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminGate = lazy(() => import('./pages/admin/AdminGate'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Founder = lazy(() => import('./pages/Founder'));
const Portfolio = lazy(() => import('./pages/Portfolio'));

// Shown while a page chunk downloads; keeps the footer from jumping up the screen.
function PageFallback() {
  return <div aria-busy="true" style={{ minHeight: '70vh' }} />;
}

class ErrorBoundary extends Component {
  state = { crashed: false };
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  render() {
    if (this.state.crashed) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Open Sans', sans-serif",
            color: '#1A1A2E',
            gap: 16,
          }}
        >
          <h2 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 22 }}>
            Something went wrong
          </h2>
          <p style={{ color: '#6C757D', fontSize: 14 }}>
            Please refresh the page or try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              background: '#00AEEF',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Scroll to top on route change, or to the target anchor if the URL has a hash
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  // Prevent the browser's own scroll restoration from fighting our scroll logic below
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname, hash]);
  return null;
}

function AppLayout() {
  useAnalytics(); // track visits & page views on the public site only
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Breadcrumbs />
      <main style={{ flex: 1 }}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/program" element={<Program />} />
            <Route path="/ideatoimpact" element={<IdeaToImpact />} />
            <Route path="/ideatoimpact/apply" element={<IdeaToImpactApply />} />
            <Route path="/project-assistance" element={<ProjectAssistance />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:jobCode" element={<CareerDetail />} />
            <Route path="/career" element={<Navigate to="/careers" replace />} />
            {/* Former standalone pages, now sections of the CampusToCorp page */}
            <Route path="/placements" element={<Navigate to="/program#placements" replace />} />
            <Route path="/colleges" element={<Navigate to="/program#colleges" replace />} />
            <Route path="/companies" element={<Navigate to="/program#companies" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/founder" element={<Founder />} />
            <Route path="/portfolio" element={<Portfolio />} />
            {/* 404 fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Admin - no Navbar/Footer */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<PageFallback />}>
                <AdminGate />
              </Suspense>
            }
          />
          {/* Public site */}
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}
